import {
	AlertTriangle,
	Check,
	CheckSquare,
	ChevronLeft,
	ChevronRight,
	FolderOpen,
	Loader2,
	Minus,
	Plus,
	RefreshCw,
	Search,
	Square,
	Trash2,
	Wand2,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type {
	MappingStats,
	MissingMapping,
	PaginatedResponse,
} from "../../types/categories";
import { Chip, ChipButton } from "../chip";
import { AddKeywordModal } from "./AddKeywordModal";
import {
	CategorySelectModal,
	type CategorySuggestion,
} from "./CategorySelectModal";

export const MissingMappingsTab = () => {
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<"all" | "keyword" | "hierarchy">(
		"all",
	);
	const [generatedContentFilter, setGeneratedContentFilter] = useState(false);
	const [page, setPage] = useState(1);
	const [data, setData] = useState<PaginatedResponse<MissingMapping> | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState<MappingStats | null>(null);

	// ── Map / Delete state ──
	const [activeItem, setActiveItem] = useState<MissingMapping | null>(null);
	const [showMapModal, setShowMapModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [mapping, setMapping] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [suggestions, setSuggestions] = useState<
		Record<number, CategorySuggestion[]>
	>({});
	const [loadingSuggestions, setLoadingSuggestions] = useState<
		Record<number, boolean>
	>({});
	const [showAddKeyword, setShowAddKeyword] = useState(false);
	// Track which suggestions have been mapped per item (keyword multi-map)
	const [mappedSuggestions, setMappedSuggestions] = useState<
		Record<number, Set<number>>
	>({});

	// ── Multi-select state ──
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [showBulkMapModal, setShowBulkMapModal] = useState(false);
	const [bulkMapping, setBulkMapping] = useState(false);

	// ── Auto-match state ──
	const [autoMatching, setAutoMatching] = useState(false);
	const [autoMatchResult, setAutoMatchResult] = useState<{
		total: number;
		matched: number;
		skipped: number;
	} | null>(null);

	// Clear selection when data changes (page, search, filter)
	// biome-ignore lint/correctness/useExhaustiveDependencies: clear selection on data change
	useEffect(() => {
		setSelectedIds(new Set());
	}, [data]);

	const toggleSelect = (id: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (selectedIds.size === items.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(items.map((i) => i.id)));
		}
	};

	// ── Bulk map handler ──
	const handleBulkMap = async (categoryId: number) => {
		if (selectedIds.size === 0) return;
		setBulkMapping(true);
		try {
			const promises = Array.from(selectedIds).map((id) =>
				api["category-mappings"]({ id: String(id) }).patch({ categoryId }),
			);
			await Promise.all(promises);
			setShowBulkMapModal(false);
			setSelectedIds(new Set());
			// Clean up cached suggestions for mapped items
			setSuggestions((prev) => {
				const next = { ...prev };
				for (const id of selectedIds) delete next[id];
				return next;
			});
			await Promise.all([fetchMissing(), fetchStats()]);
		} catch {
			/* ignore — will stay in list */
		} finally {
			setBulkMapping(false);
		}
	};

	// Fetch stats
	const fetchStats = useCallback(async () => {
		try {
			const { data } = await api["category-mappings"].stats.get();
			if (data) setStats(data as unknown as MappingStats);
		} catch {
			/* ignore */
		}
	}, []);

	// ── Auto-match hierarchical paths ──
	const handleAutoMatch = async () => {
		setAutoMatching(true);
		setAutoMatchResult(null);
		try {
			const { data } = await api["category-mappings"]["auto-match"].post();
			const json = data as unknown as {
				success: boolean;
				total: number;
				matched: number;
				skipped: number;
			};
			if (json) setAutoMatchResult(json);
			// Refresh the list and stats
			setSuggestions({});
			await Promise.all([fetchMissing(), fetchStats()]);
		} catch {
			/* ignore */
		} finally {
			setAutoMatching(false);
		}
	};

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	// Fetch missing mappings
	const fetchMissing = useCallback(async () => {
		setLoading(true);
		try {
			const query: Record<string, string> = { page: String(page) };
			if (search.trim()) query.q = search.trim();
			if (typeFilter !== "all") query.type = typeFilter;
			if (generatedContentFilter) query.generatedContent = "true";
			const { data } = await api["category-mappings"].missing.get({ query });
			if (data) setData(data as unknown as PaginatedResponse<MissingMapping>);
		} catch {
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [page, search, typeFilter, generatedContentFilter]);

	// Debounced fetch
	const fetchDebounce = useRef<ReturnType<typeof setTimeout>>(undefined);
	useEffect(() => {
		clearTimeout(fetchDebounce.current);
		fetchDebounce.current = setTimeout(() => {
			fetchMissing();
		}, 300);
		return () => clearTimeout(fetchDebounce.current);
	}, [fetchMissing]);

	// Reset page when search changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset page on search/filter change
	useEffect(() => {
		setPage(1);
	}, [search, typeFilter, generatedContentFilter]);

	// ── Fetch suggestions for a single item ──
	const fetchSuggestions = useCallback(
		async (item: MissingMapping) => {
			if (suggestions[item.id] || loadingSuggestions[item.id]) return;
			setLoadingSuggestions((prev) => ({ ...prev, [item.id]: true }));
			try {
				const { data } = await api["category-mappings"]({
					id: String(item.id),
				}).suggestions.get();
				const json = (data ?? []) as unknown as CategorySuggestion[];
				setSuggestions((prev) => ({ ...prev, [item.id]: json }));
			} catch {
				setSuggestions((prev) => ({ ...prev, [item.id]: [] }));
			} finally {
				setLoadingSuggestions((prev) => ({ ...prev, [item.id]: false }));
			}
		},
		[suggestions, loadingSuggestions],
	);

	// ── Auto-load suggestions for all visible items ──
	// biome-ignore lint/correctness/useExhaustiveDependencies: trigger on data items change
	useEffect(() => {
		if (!data?.items) return;
		for (const item of data.items) {
			if (!suggestions[item.id] && !loadingSuggestions[item.id]) {
				fetchSuggestions(item);
			}
		}
	}, [data?.items]);

	// ── Helper: remove an item locally (fire-and-forget) ──
	const removeItemLocally = (id: number) => {
		setData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				items: prev.items.filter((i) => i.id !== id),
				total: prev.total - 1,
			};
		});
		setSuggestions((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
		setStats((prev) =>
			prev
				? {
						...prev,
						missingMappings: prev.missingMappings - 1,
						totalMappings: prev.totalMappings + 1,
					}
				: prev,
		);
	};

	// ── Quick-map from suggestion ──
	const handleQuickMap = async (item: MissingMapping, categoryId: number) => {
		setMapping(true);
		try {
			const { error } = await api["category-mappings"]({
				id: String(item.id),
			}).patch({ categoryId });
			if (error) throw new Error("Map failed");

			if (item.type === "keyword") {
				// Keywords: mark chip as mapped, stay in list for more picks
				setMappedSuggestions((prev) => {
					const next = { ...prev };
					const set = new Set(prev[item.id]);
					set.add(categoryId);
					next[item.id] = set;
					return next;
				});
			} else {
				// Hierarchy: remove locally, fire-and-forget
				removeItemLocally(item.id);
			}
		} catch {
			/* ignore — stays in list */
		} finally {
			setMapping(false);
		}
	};

	// ── Map via CategorySelectModal ──
	const handleMapFromModal = async (categoryId: number) => {
		if (!activeItem) return;
		setMapping(true);
		try {
			const { error } = await api["category-mappings"]({
				id: String(activeItem.id),
			}).patch({ categoryId });
			if (error) throw new Error("Map failed");
			setShowMapModal(false);
			removeItemLocally(activeItem.id);
			setActiveItem(null);
		} catch {
			/* ignore */
		} finally {
			setMapping(false);
		}
	};

	// ── Delete mapping ──
	const handleDelete = async () => {
		if (!activeItem) return;
		setDeleting(true);
		try {
			const { error } = await api["category-mappings"]({
				id: String(activeItem.id),
			}).delete();
			if (error) throw new Error("Delete failed");
			setShowDeleteConfirm(false);
			removeItemLocally(activeItem.id);
			setActiveItem(null);
		} catch {
			/* ignore */
		} finally {
			setDeleting(false);
		}
	};

	// ── Accept category suggestion (maps it) ──
	const handleAcceptSuggestion = async (item: MissingMapping) => {
		if (!item.categorySuggestion) return;
		setMapping(true);
		try {
			const { error } = await api["category-mappings"]({
				id: String(item.id),
			}).patch({ categoryId: item.categorySuggestion });
			if (error) throw new Error("Accept failed");
			removeItemLocally(item.id);
		} catch {
			/* ignore */
		} finally {
			setMapping(false);
		}
	};

	// ── Deny category suggestion (clears it) ──
	const handleDenySuggestion = async (item: MissingMapping) => {
		try {
			await api["category-mappings"]({ id: String(item.id) })[
				"deny-suggestion"
			].patch();
			// Update locally to remove the suggestion from the item
			setData((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					items: prev.items.map((i) =>
						i.id === item.id
							? { ...i, categorySuggestion: null, categorySuggestionName: null }
							: i,
					),
				};
			});
		} catch {
			/* ignore */
		}
	};

	const rawItems = data?.items ?? [];

	// Sort items so entries with suggestions come first
	const items = useMemo(() => {
		return [...rawItems].sort((a, b) => {
			// Items with category_suggestion from auto-match come first
			const aSug = a.categorySuggestion ? 1 : 0;
			const bSug = b.categorySuggestion ? 1 : 0;
			if (bSug !== aSug) return bSug - aSug;
			// Then sort by best AI suggestion score
			const aTopScore = suggestions[a.id]?.[0]?.score ?? -1;
			const bTopScore = suggestions[b.id]?.[0]?.score ?? -1;
			return bTopScore - aTopScore;
		});
	}, [rawItems, suggestions]);

	return (
		<div className="space-y-5">
			{/* Stats bar */}
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div className="bg-white rounded-xl border border-slate-200/60 p-5">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center">
							<AlertTriangle className="w-5 h-5 text-danger" />
						</div>
						<div>
							<p className="text-2xl font-bold text-slate-800">
								{stats?.missingMappings ?? "…"}
							</p>
							<p className="text-xs text-slate-500">Missing mappings</p>
						</div>
					</div>
				</div>
				<div className="bg-white rounded-xl border border-slate-200/60 p-5">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
							<svg
								className="w-5 h-5 text-success"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								role="img"
								aria-label="Resolved"
							>
								<title>Resolved</title>
								<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
								<polyline points="22 4 12 14.01 9 11.01" />
							</svg>
						</div>
						<div>
							<p className="text-2xl font-bold text-slate-800">
								{stats?.totalMappings ?? "…"}
							</p>
							<p className="text-xs text-slate-500">Resolved mappings</p>
						</div>
					</div>
				</div>
				<div className="bg-white rounded-xl border border-slate-200/60 p-5">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
							<svg
								className="w-5 h-5 text-info"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								role="img"
								aria-label="Categories"
							>
								<title>Categories</title>
								<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
							</svg>
						</div>
						<div>
							<p className="text-2xl font-bold text-slate-800">
								{stats?.totalCategories ?? "…"}
							</p>
							<p className="text-xs text-slate-500">Total categories</p>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="bg-white rounded-xl border border-slate-200/60 p-4">
				<div className="flex flex-wrap items-center gap-3">
					<div className="relative flex-1 min-w-50">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
						<input
							type="text"
							placeholder="Search keywords or paths..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:bg-white transition-all"
						/>
					</div>
					{/* Type filter chips */}
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setTypeFilter("all")}
							className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
								typeFilter === "all"
									? "bg-primary text-white border-primary shadow-sm"
									: "bg-slate-50 text-slate-500 border-slate-200/80 hover:bg-slate-100 hover:text-slate-700"
							}`}
						>
							All
						</button>
						<button
							type="button"
							onClick={() => setTypeFilter("keyword")}
							className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
								typeFilter === "keyword"
									? "bg-info/10 text-info border-info/40 shadow-sm"
									: "bg-slate-50 text-slate-500 border-slate-200/80 hover:bg-slate-100 hover:text-slate-700"
							}`}
						>
							Keywords
						</button>
						<button
							type="button"
							onClick={() => setTypeFilter("hierarchy")}
							className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
								typeFilter === "hierarchy"
									? "bg-warning/10 text-warning-dark border-warning-dark/40 shadow-sm"
									: "bg-slate-50 text-slate-500 border-slate-200/80 hover:bg-slate-100 hover:text-slate-700"
							}`}
						>
							Hierarchical
						</button>
						<div className="w-px h-5 bg-slate-200" />
						<button
							type="button"
							onClick={() => setGeneratedContentFilter((prev) => !prev)}
							className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
								generatedContentFilter
									? "bg-purple-100 text-purple-700 border-purple-300 shadow-sm"
									: "bg-slate-50 text-slate-500 border-slate-200/80 hover:bg-slate-100 hover:text-slate-700"
							}`}
						>
							Generated Content
						</button>
					</div>
					{/* Add keyword CTA */}
					<div className="ml-auto flex items-center gap-2">
						<button
							type="button"
							disabled={autoMatching}
							onClick={handleAutoMatch}
							className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-success/10 text-success border border-success/30 text-xs font-semibold hover:bg-success/20 transition-colors shadow-sm disabled:opacity-50"
						>
							{autoMatching ? (
								<Loader2 className="w-3.5 h-3.5 animate-spin" />
							) : (
								<Wand2 className="w-3.5 h-3.5" />
							)}
							{autoMatching ? "Matching…" : "Auto-match Paths"}
						</button>
						<button
							type="button"
							onClick={() => setShowAddKeyword(true)}
							className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-black text-xs font-semibold hover:bg-brand-hover transition-colors"
						>
							<Plus className="w-3.5 h-3.5" />
							Add Keyword
						</button>
					</div>
				</div>
			</div>

			{/* Auto-match result banner */}
			{autoMatchResult && (
				<div className="bg-white rounded-xl border border-success/30 p-4 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="w-9 h-9 rounded-xl bg-success/10 flex items-center justify-center">
							<Wand2 className="w-4.5 h-4.5 text-success" />
						</div>
						<div>
							<p className="text-sm font-semibold text-slate-700">
								Auto-match complete
							</p>
							<p className="text-xs text-slate-500">
								<span className="font-semibold text-success">
									{autoMatchResult.matched}
								</span>{" "}
								matched
								{" · "}
								<span className="font-semibold text-slate-600">
									{autoMatchResult.skipped}
								</span>{" "}
								skipped
								{" · "}
								{autoMatchResult.total} total checked
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={() => setAutoMatchResult(null)}
						className="text-slate-400 hover:text-slate-600 transition-colors"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
			)}

			{/* Results count */}
			<div className="flex items-center justify-between px-1">
				<p className="text-sm text-slate-500">
					{loading ? (
						"Loading…"
					) : (
						<>
							<span className="font-semibold text-slate-700">
								{data?.total ?? 0}
							</span>{" "}
							missing mapping{data?.total !== 1 ? "s" : ""}
						</>
					)}
				</p>
				<div className="flex items-center gap-2">
					{data && data.totalPages > 1 && (
						<p className="text-xs text-slate-400">
							Page {data.page} of {data.totalPages}
						</p>
					)}
					<button
						type="button"
						disabled={loading}
						onClick={() => {
							fetchMissing();
							fetchStats();
						}}
						className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-40"
						title="Refresh"
					>
						<RefreshCw
							className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
						/>
					</button>
				</div>
			</div>

			{/* Missing mappings table */}
			{loading ? (
				<div className="bg-white rounded-xl border border-slate-200/60 p-12 flex items-center justify-center">
					<Loader2 className="w-6 h-6 text-primary animate-spin" />
				</div>
			) : items.length === 0 ? (
				<div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
					<div className="w-14 h-14 rounded-2xl bg-success/10 flex items-center justify-center mx-auto mb-4">
						<AlertTriangle className="w-7 h-7 text-success" />
					</div>
					<h3 className="text-lg font-semibold text-slate-700">All clear!</h3>
					<p className="text-sm text-slate-400 mt-1">
						No missing mappings match your filters.
					</p>
				</div>
			) : (
				<div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
					<table className="w-full">
						<thead>
							<tr className="border-b border-slate-100 bg-slate-50/50">
								{" "}
								<th className="pl-5 pr-2 py-3.5 w-10">
									<button
										type="button"
										onClick={toggleSelectAll}
										className="text-slate-400 hover:text-primary transition-colors"
										title={
											selectedIds.size === items.length
												? "Deselect all"
												: "Select all"
										}
									>
										{selectedIds.size === items.length && items.length > 0 ? (
											<CheckSquare className="w-4 h-4" />
										) : selectedIds.size > 0 ? (
											<Minus className="w-4 h-4" />
										) : (
											<Square className="w-4 h-4" />
										)}
									</button>
								</th>{" "}
								<th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
									#
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Keyword / Hierarchical Path
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Type
								</th>
								<th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Suggestions
								</th>
								<th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{items.map((item) => {
								const itemSuggestions = suggestions[item.id];
								const isSugLoading = loadingSuggestions[item.id];
								return (
									<tr
										key={item.id}
										className={`transition-colors ${selectedIds.has(item.id) ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50/60"}`}
									>
										<td className="pl-5 pr-2 py-4 w-10">
											<button
												type="button"
												onClick={() => toggleSelect(item.id)}
												className={`transition-colors ${
													selectedIds.has(item.id)
														? "text-primary"
														: "text-slate-300 hover:text-slate-500"
												}`}
											>
												{selectedIds.has(item.id) ? (
													<CheckSquare className="w-4 h-4" />
												) : (
													<Square className="w-4 h-4" />
												)}
											</button>
										</td>
										<td className="px-5 py-4 text-xs font-mono text-slate-400">
											{item.id}
										</td>
										<td className="px-5 py-4">
											{item.type === "hierarchy" && item.hierarchicalPath ? (
												<div className="flex items-center gap-1 flex-wrap">
													{item.hierarchicalPath
														.split(/\s*(?:>|\|\||\|)\s*/)
														.map((part, i) => (
															<span
																key={part}
																className="flex items-center gap-1"
															>
																{i > 0 && (
																	<ChevronRight className="w-3 h-3 text-slate-300" />
																)}
																<Chip variant="warning" size="sm">
																	{part}
																</Chip>
															</span>
														))}
												</div>
											) : (
												<Chip variant="info" size="sm">
													{item.keyword ?? item.hierarchicalPath}
												</Chip>
											)}
										</td>
										<td className="px-5 py-4">
											<Chip
												variant={item.type === "hierarchy" ? "warning" : "info"}
												size="xs"
											>
												{item.type}
											</Chip>
										</td>
										{/* Suggestions column */}
										<td className="px-5 py-4">
											<div className="flex flex-col gap-2">
												{/* ── Category suggestion from auto-match ── */}
												{item.categorySuggestion &&
													item.categorySuggestionName && (
														<div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200/60">
															<Wand2 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
															<span
																className="text-xs font-medium text-amber-800 truncate max-w-45"
																title={item.categorySuggestionName}
															>
																{item.categorySuggestionName}
															</span>
															<div className="flex items-center gap-0.5 ml-auto shrink-0">
																<button
																	type="button"
																	disabled={mapping}
																	onClick={() => handleAcceptSuggestion(item)}
																	className="w-6 h-6 rounded-md flex items-center justify-center text-success bg-success/10 hover:bg-success/20 transition-colors disabled:opacity-50"
																	title="Accept suggestion"
																>
																	<Check className="w-3.5 h-3.5" />
																</button>
																<button
																	type="button"
																	onClick={() => handleDenySuggestion(item)}
																	className="w-6 h-6 rounded-md flex items-center justify-center text-danger bg-danger/10 hover:bg-danger/20 transition-colors"
																	title="Dismiss suggestion"
																>
																	<X className="w-3.5 h-3.5" />
																</button>
															</div>
														</div>
													)}
												{/* ── AI suggestions ── */}
												{isSugLoading || (!itemSuggestions && !isSugLoading) ? (
													<div className="flex items-center gap-2">
														<Loader2 className="w-3.5 h-3.5 text-primary/40 animate-spin" />
														<span className="text-[11px] text-slate-400">
															Finding…
														</span>
													</div>
												) : itemSuggestions && itemSuggestions.length > 0 ? (
													<div className="flex flex-wrap gap-2">
														{itemSuggestions.map((sug) => {
															const isMapped = mappedSuggestions[item.id]?.has(
																sug.id,
															);
															return (
																<ChipButton
																	key={sug.id}
																	disabled={mapping || isMapped}
																	onClick={() => handleQuickMap(item, sug.id)}
																	title={sug.fullPath}
																	variant={
																		isMapped
																			? "success"
																			: sug.score >= 0.7
																				? "success"
																				: sug.score >= 0.4
																					? "warning"
																					: "slate"
																	}
																	className={`gap-1.5 ${isMapped ? "opacity-60" : ""}`}
																>
																	{isMapped && <Check className="w-3 h-3" />}
																	{sug.name}
																	<span
																		className={`text-[11px] font-mono opacity-70`}
																	>
																		{sug.score.toFixed(2)}
																	</span>
																</ChipButton>
															);
														})}
													</div>
												) : !item.categorySuggestion ? (
													<span className="text-[11px] text-slate-400 italic">
														No matches
													</span>
												) : null}
											</div>
										</td>
										{/* Actions column */}
										<td className="px-5 py-4">
											<div className="flex items-center justify-end gap-2">
												<button
													type="button"
													onClick={() => {
														setActiveItem(item);

														setShowMapModal(true);
													}}
													className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
												>
													<FolderOpen className="w-3.5 h-3.5" /> Map
												</button>
												<button
													type="button"
													onClick={() => {
														setActiveItem(item);
														setShowDeleteConfirm(true);
													}}
													className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-danger bg-danger/5 hover:bg-danger/10 transition-colors"
												>
													<Trash2 className="w-3.5 h-3.5" /> Delete
												</button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Pagination */}
			{data && data.totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 pt-2">
					<button
						type="button"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
						className="flex items-center gap-1 px-3.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
					>
						<ChevronLeft className="w-4 h-4" />
						Previous
					</button>
					<div className="flex items-center gap-1">
						{Array.from({ length: Math.min(data.totalPages, 7) }, (_, i) => {
							let pageNum: number;
							if (data.totalPages <= 7) {
								pageNum = i + 1;
							} else if (page <= 4) {
								pageNum = i + 1;
							} else if (page >= data.totalPages - 3) {
								pageNum = data.totalPages - 6 + i;
							} else {
								pageNum = page - 3 + i;
							}
							return (
								<button
									key={pageNum}
									type="button"
									onClick={() => setPage(pageNum)}
									className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
										pageNum === page
											? "bg-primary text-white"
											: "text-slate-500 hover:bg-slate-100"
									}`}
								>
									{pageNum}
								</button>
							);
						})}
					</div>
					<button
						type="button"
						disabled={page >= data.totalPages}
						onClick={() => setPage((p) => p + 1)}
						className="flex items-center gap-1 px-3.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
					>
						Next
						<ChevronRight className="w-4 h-4" />
					</button>
				</div>
			)}

			{/* ── Floating Bulk-Action Bar ── */}
			{selectedIds.size > 0 && (
				<div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 bg-white rounded-2xl border border-slate-200 shadow-xl">
					<span className="text-sm font-semibold text-slate-700">
						{selectedIds.size} selected
					</span>
					<div className="w-px h-5 bg-slate-200" />
					<button
						type="button"
						onClick={() => setShowBulkMapModal(true)}
						className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-black bg-brand hover:bg-brand-hover transition-colors"
					>
						<FolderOpen className="w-3.5 h-3.5" />
						Map to Category
					</button>
					<button
						type="button"
						onClick={() => setSelectedIds(new Set())}
						className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
					>
						<X className="w-3.5 h-3.5" />
						Clear
					</button>
				</div>
			)}

			{/* ── Bulk Map Modal ── */}
			<CategorySelectModal
				open={showBulkMapModal}
				onClose={() => setShowBulkMapModal(false)}
				onSelect={handleBulkMap}
				title={`Map ${selectedIds.size} item${selectedIds.size !== 1 ? "s" : ""} to category`}
				selecting={bulkMapping}
			/>

			{/* ── Category Select Modal (single item) ── */}
			<CategorySelectModal
				open={showMapModal}
				onClose={() => {
					setShowMapModal(false);
					setActiveItem(null);
				}}
				onSelect={handleMapFromModal}
				title={
					activeItem
						? `Map "${activeItem.keyword ?? activeItem.hierarchicalPath}" to category`
						: "Select Category"
				}
				suggestions={activeItem ? suggestions[activeItem.id] : undefined}
				selecting={mapping}
			/>

			{/* ── Delete Confirmation Popup ── */}
			{showDeleteConfirm && activeItem && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
					<div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
						<div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
							<h3 className="text-sm font-semibold text-slate-700">
								Delete Mapping
							</h3>
							<button
								type="button"
								onClick={() => {
									setShowDeleteConfirm(false);
									setActiveItem(null);
								}}
								className="text-slate-400 hover:text-slate-600 transition-colors"
							>
								<X className="w-5 h-5" />
							</button>
						</div>
						<div className="p-6">
							<div className="flex items-start gap-3 mb-4">
								<div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center shrink-0">
									<Trash2 className="w-5 h-5 text-danger" />
								</div>
								<div>
									<p className="text-sm text-slate-700">
										Are you sure you want to delete this mapping?
									</p>
									<p className="text-xs text-slate-400 mt-1 break-all">
										{activeItem.type === "hierarchy"
											? activeItem.hierarchicalPath
											: activeItem.keyword}
									</p>
									<p className="text-xs text-danger/70 mt-2">
										This action cannot be undone. The mapping will be
										permanently removed.
									</p>
								</div>
							</div>
							<div className="flex items-center justify-end gap-2 pt-2">
								<button
									type="button"
									onClick={() => {
										setShowDeleteConfirm(false);
										setActiveItem(null);
									}}
									className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
								>
									Cancel
								</button>
								<button
									type="button"
									disabled={deleting}
									onClick={handleDelete}
									className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-danger hover:bg-danger/90 transition-colors disabled:opacity-50"
								>
									{deleting ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										"Delete"
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Add keyword modal */}
			<AddKeywordModal
				open={showAddKeyword}
				onClose={() => setShowAddKeyword(false)}
				onCreated={() => {
					fetchMissing();
					fetchStats();
				}}
			/>
		</div>
	);
};
